import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Pill, Loader2, Search, ArrowRight, ChevronDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { fetchMedications } from "@/lib/api";
import { searchWithFallback } from "@/lib/search";
import { INITIAL_ITEMS } from "@/lib/paginate";
import { diseaseName } from "@/data/content";
import { MED } from "@/constants/testIds";

export default function KnowYourMedications() {
  const [meds, setMeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await fetchMedications();
        setMeds(data || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const { items: filtered, fallback, empty } = useMemo(
    () =>
      searchWithFallback(
        meds,
        q,
        (m) => `${m.name} ${m.drug_class || ""} ${diseaseName(m.disease || "")} ${m.uses || ""}`
      ),
    [meds, q]
  );

  // Alphabetised, then grouped by first letter — used for the expanded view.
  const sortedFiltered = useMemo(
    () => [...filtered].sort((a, b) => a.name.localeCompare(b.name)),
    [filtered]
  );
  const groups = useMemo(() => {
    const g = {};
    for (const m of sortedFiltered) {
      const letter = (m.name[0] || "#").toUpperCase();
      (g[letter] = g[letter] || []).push(m);
    }
    return Object.entries(g).sort(([a], [b]) => a.localeCompare(b));
  }, [sortedFiltered]);

  // Collapsed view: first 4 medications alphabetically.
  const latestFew = useMemo(
    () => sortedFiltered.slice(0, INITIAL_ITEMS),
    [sortedFiltered]
  );

  const searching = q.trim().length > 0;
  const showAll = expanded || searching;
  const hasMore = filtered.length > latestFew.length;

  return (
    <main data-testid={MED.hub} className="max-w-5xl mx-auto px-6 md:px-10 py-16 md:py-24">
      <div className="max-w-3xl">
        <div className="text-xs tracking-[0.2em] uppercase text-slate-500">
          Know your medications
        </div>
        <h1 className="font-serif-display text-5xl md:text-6xl mt-3 text-slate-900 leading-tight">
          A to Z of common medications.
        </h1>
        <p className="text-slate-600 mt-6 text-lg leading-relaxed">
          Search a medication or browse alphabetically. Tap any name to read what it does,
          when it's used and what to watch for.
        </p>
      </div>

      <div className="mt-10 relative max-w-lg">
        <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          data-testid={MED.search}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search e.g. Metformin, Statin, Diabetes…"
          className="pl-11 h-12 rounded-full bg-white"
        />
      </div>

      {loading ? (
        <div className="mt-16 flex items-center text-slate-500">
          <Loader2 className="animate-spin mr-2" /> Loading medications…
        </div>
      ) : (
        <>
          {fallback ? (
            <p
              data-testid="med-search-fallback"
              className="mt-6 text-sm text-slate-500"
            >
              {empty
                ? `No exact match for "${q}". Showing all medications instead.`
                : `No exact match for "${q}". Showing related medications instead.`}
            </p>
          ) : null}

          {showAll ? (
            <div className="mt-10 space-y-12">
              {groups.map(([letter, items]) => (
                <section key={letter}>
                  <div className="flex items-center gap-4">
                    <div className="font-serif-display text-5xl text-[hsl(var(--primary-bold))] leading-none">
                      {letter}
                    </div>
                    <div className="h-px flex-1 bg-slate-200" />
                  </div>
                  <ul className="mt-6 grid sm:grid-cols-2 gap-3">
                    {items.map((m) => (
                      <MedRow key={m.id} m={m} />
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          ) : (
            <div className="mt-10">
              <ul className="grid sm:grid-cols-2 gap-3">
                {latestFew.map((m) => (
                  <MedRow key={m.id} m={m} />
                ))}
              </ul>
              {hasMore ? (
                <div className="mt-10 flex justify-center">
                  <Button
                    data-testid="med-show-more"
                    variant="outline"
                    onClick={() => setExpanded(true)}
                    className="rounded-full h-11 px-6"
                  >
                    Show all {filtered.length} medications
                    <ChevronDown size={16} className="ml-2" />
                  </Button>
                </div>
              ) : null}
            </div>
          )}
        </>
      )}
    </main>
  );
}

function MedRow({ m }) {
  return (
    <li>
      <Link
        to={`/know-your-medications/${m.id}`}
        data-testid={MED.card(m.id)}
        className="group flex items-center gap-4 p-5 rounded-2xl border border-slate-200 bg-white hover:border-slate-300 hover:shadow-md transition-all"
      >
        <div className="w-10 h-10 rounded-xl bg-[hsl(var(--primary-bold))]/20 grid place-items-center text-slate-900 shrink-0">
          <Pill size={18} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-serif-display text-xl text-slate-900 leading-tight">
            {m.name}
          </div>
        </div>
        <ArrowRight
          size={16}
          className="text-slate-400 group-hover:translate-x-0.5 transition-transform"
        />
      </Link>
    </li>
  );
}
